import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import {
  Megaphone,
  Sparkles,
  Handshake,
  Wand2,
  MessageSquare,
  Wallet,
  ArrowRight,
} from "lucide-react";

/**
 * Clean 6-step "How it works" showcase. NO pinned scroll, NO 600vh sticky,
 * NO scroll-scrubbing content. Standard vertical scroll. Renders as a
 * responsive premium grid: 1 col mobile, 2 col md, 3 col lg.
 */

const STEPS = [
  {
    key: "campaign",
    who: "brand",
    whoLabel: "Brand acts",
    icon: Megaphone,
    tag: "Step 01",
    title: "Post a brief in 5 minutes",
    body: "Drop your budget, deliverables and platforms — Nexus turns it into an applicant-ranked listing that goes live instantly.",
    accent: "from-[#00D4FF] to-[#3B82F6]",
    glow: "rgba(0,212,255,0.25)",
  },
  {
    key: "discover",
    who: "both",
    whoLabel: "Creator & Brand",
    icon: Sparkles,
    tag: "Step 02",
    title: "Match with verified creators",
    body: "Every applicant is auto-ranked 0–100 for niche fit, engagement and delivery track record. Verified ID badges included.",
    accent: "from-[#FF107A] to-[#D946EF]",
    glow: "rgba(255,16,122,0.25)",
  },
  {
    key: "escrow",
    who: "both",
    whoLabel: "Both parties",
    icon: Handshake,
    tag: "Step 03",
    title: "Escrow locks the funds",
    body: "Brand funds escrow before work begins. Creators work with full confidence the money is safely held.",
    accent: "from-emerald-400 to-[#00D4FF]",
    glow: "rgba(52,211,153,0.25)",
  },
  {
    key: "studio",
    who: "creator",
    whoLabel: "Creator acts",
    icon: Wand2,
    tag: "Step 04",
    title: "Edit inside Creator Studio",
    body: "Built-in browser studio with Whisper auto-captions, AI auto-clips, chroma-key green screen and brand asset vaults.",
    accent: "from-[#9D4EDD] to-[#FF107A]",
    glow: "rgba(157,78,221,0.28)",
  },
  {
    key: "review",
    who: "brand",
    whoLabel: "Brand acts",
    icon: MessageSquare,
    tag: "Step 05",
    title: "Timestamped review & approval",
    body: "Scrub the draft, drop timestamped notes, hit Approve — escrow releases the same second. No email chains, no ghosting.",
    accent: "from-amber-400 to-[#FF107A]",
    glow: "rgba(252,211,77,0.25)",
  },
  {
    key: "payout",
    who: "creator",
    whoLabel: "Creator acts",
    icon: Wallet,
    tag: "Step 06",
    title: "Instant payout + XP",
    body: "Bank transfer in hours, not weeks. Every completed hire earns XP, public reviews and Hall-of-Fame badges.",
    accent: "from-emerald-400 to-[#9D4EDD]",
    glow: "rgba(52,211,153,0.28)",
  },
];

const whoStyle = {
  brand: "text-[#00D4FF] border-[#00D4FF]/25 bg-[#00D4FF]/8",
  creator: "text-[#FF107A] border-[#FF107A]/25 bg-[#FF107A]/8",
  both: "text-emerald-300 border-emerald-500/25 bg-emerald-500/8",
};

export default function WorkflowShowcase() {
  return (
    <section
      id="how-it-works"
      className="relative py-24 md:py-32 border-t border-white/[0.06]"
      data-testid="workflow-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 h-7 px-3 rounded-full border border-white/10 bg-white/[0.03] text-[10px] uppercase tracking-[0.28em] text-zinc-300 font-semibold mb-5">
            <span className="h-1.5 w-1.5 rounded-full bg-nexus-pink animate-pulse" />
            How Nexus works
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white mb-4">
            The full marketplace loop.{" "}
            <span className="bg-gradient-to-r from-[#FF107A] via-[#9D4EDD] to-[#00D4FF] bg-clip-text text-transparent">
              Six clean steps.
            </span>
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed">
            Brief to bank transfer — no email chains, no 90-day invoicing, no
            ghosting. Every step is signed off before the next begins.
          </p>
        </div>

        {/* 6-step grid */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((s, i) => (
            <StepCard key={s.key} step={s} index={i} />
          ))}
        </div>

        {/* Bottom CTA rail */}
        <div className="mt-16 md:mt-24 rounded-3xl border border-white/10 bg-gradient-to-br from-[#FF107A]/10 via-[#9D4EDD]/5 to-[#00D4FF]/10 p-8 md:p-12 relative overflow-hidden">
          <div
            className="absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl opacity-40"
            style={{ background: "rgba(255,16,122,0.4)" }}
          />
          <div
            className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full blur-3xl opacity-30"
            style={{ background: "rgba(0,212,255,0.4)" }}
          />

          <div className="relative grid md:grid-cols-[1fr_auto_1fr] items-center gap-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#00D4FF] mb-2 font-semibold">
                For brands
              </div>
              <div className="font-display font-bold text-2xl md:text-3xl tracking-tight text-white mb-3">
                Hire without the risk.
              </div>
              <p className="text-sm text-zinc-300/90 mb-5 max-w-sm">
                Post your first brief in 5 minutes. Money stays in escrow until
                you approve. Cancel anytime.
              </p>
              <Link to="/auth?mode=signup&role=brand" data-testid="workflow-cta-brand">
                <Button className="rounded-full h-11 px-6 nexus-gradient text-white border-0 font-semibold shadow-[0_10px_40px_-10px_rgba(255,16,122,0.5)]">
                  Start as a brand <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="hidden md:flex items-center justify-center">
              <div className="h-20 w-px bg-white/10" />
            </div>

            <div className="md:text-right">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF107A] mb-2 font-semibold">
                For creators
              </div>
              <div className="font-display font-bold text-2xl md:text-3xl tracking-tight text-white mb-3">
                Get paid for real.
              </div>
              <p className="text-sm text-zinc-300/90 mb-5 md:ml-auto max-w-sm">
                Set your rate. Land deals. Never lift a finger before the
                money's locked in escrow.
              </p>
              <Link to="/auth?mode=signup&role=creator" data-testid="workflow-cta-creator">
                <Button
                  variant="outline"
                  className="rounded-full h-11 px-6 bg-white/[0.04] border-white/15 text-white hover:bg-white/[0.08] hover:text-white font-semibold"
                >
                  Start as a creator <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------- Individual step card ------- */

function StepCard({ step, index }) {
  const Icon = step.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: index * 0.06, ease: "easeOut" }}
      className="group relative rounded-3xl border border-white/[0.07] bg-white/[0.02] backdrop-blur-sm p-6 md:p-7 overflow-hidden hover:border-white/15 hover:bg-white/[0.035]"
      style={{ transition: "border-color 250ms, background-color 250ms" }}
      data-testid={`workflow-step-${step.key}`}
    >
      {/* Ambient glow that appears on hover */}
      <div
        className="absolute -top-16 -right-16 h-40 w-40 rounded-full blur-3xl opacity-0 group-hover:opacity-100"
        style={{ background: step.glow, transition: "opacity 400ms" }}
      />

      {/* Step number watermark */}
      <div className="absolute top-5 right-5 font-display font-black text-5xl text-white/[0.06] leading-none tabular-nums select-none">
        {String(index + 1).padStart(2, "0")}
      </div>

      <div className="relative space-y-4">
        {/* Who acts pill */}
        <div
          className={`inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full border text-[10px] uppercase tracking-widest font-bold ${whoStyle[step.who]}`}
        >
          {step.whoLabel}
        </div>

        {/* Icon */}
        <div
          className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${step.accent} flex items-center justify-center shadow-lg`}
        >
          <Icon className="h-5 w-5 text-white" />
        </div>

        {/* Tag + Title */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-1 font-semibold">
            {step.tag}
          </div>
          <h3 className="font-display font-bold text-xl md:text-[22px] tracking-tight text-white leading-[1.15]">
            {step.title}
          </h3>
        </div>

        {/* Body */}
        <p className="text-sm text-zinc-400 leading-relaxed">{step.body}</p>
      </div>
    </motion.div>
  );
}
